import React, { useCallback, useEffect, useMemo, useState } from "react";
import { fetchUnbilled, saveUnbilledComment, exportXlsx, listUsers } from "../api";
import ManagerCell from "../components/ManagerCell";
import {
  PageHeader, Card, Stat, Field, Select, SortTh, sortRows, Meter,
  Loading, ErrorState, EmptyState,
} from "../components/ui";
import { money0, money, pct, num } from "../lib/format";

export default function Unbilled({ username }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [project, setProject] = useState("");
  const [sort, setSort] = useState({ key: "unbilled", dir: "desc" });
  const isFd = String(username).toLowerCase() === "fd";
  const [manager, setManager] = useState("");
  const [allManagers, setAllManagers] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [savedId, setSavedId] = useState(null);
  const [notice, setNotice] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    fetchUnbilled(String(username).split(":")[0])
      .then((data) => setRows(data || []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [username]);

  useEffect(load, [load]);

  useEffect(() => {
    if (!isFd) return;
    listUsers()
      .then((us) => setAllManagers(us.map((u) => u.username).filter((u) => u !== "fd").sort()))
      .catch(() => {});
  }, [isFd]);

  /** Swap one row in place after fd reallocates it. */
  const applyReallocation = (id, updated) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...updated } : r)));

  const projects = useMemo(
    () => [...new Set(
      rows.filter((r) => !manager || r.username === manager)
          .map((r) => r.project_name).filter(Boolean)
    )].sort(),
    [rows, manager]
  );

  const managers = useMemo(
    () => [...new Set(
      [...rows.map((r) => r.username), manager].filter(Boolean)
    )].sort(),
    [rows, manager]
  );

  const filtered = useMemo(
    () => rows.filter(
      (r) => (!project || r.project_name === project) && (!manager || r.username === manager)
    ),
    [rows, project, manager]
  );

  const sorted = useMemo(
    () =>
      sortRows(filtered, sort, {
        revenue: (r) => num(r.revenue),
        total_billed: (r) => num(r.total_billed),
        unbilled: (r) => num(r.unbilled),
        profit: (r) => num(r.profit),
      }),
    [filtered, sort]
  );

  const totals = useMemo(
    () =>
      filtered.reduce(
        (acc, r) => ({
          revenue: acc.revenue + num(r.revenue),
          billed: acc.billed + num(r.total_billed),
          unbilled: acc.unbilled + num(r.unbilled),
          profit: acc.profit + num(r.profit),
        }),
        { revenue: 0, billed: 0, unbilled: 0, profit: 0 }
      ),
    [filtered]
  );

  const editComment = (id, value) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, comments: value } : r)));

  const commitComment = async (row) => {
    setSavingId(row.id);
    setNotice("");
    try {
      await saveUnbilledComment(row.id, row.comments ?? "");
      setSavedId(row.id);
      setTimeout(() => setSavedId((c) => (c === row.id ? null : c)), 1800);
    } catch (e) {
      setNotice(`Could not save comment: ${e.message}`);
    } finally {
      setSavingId(null);
    }
  };

  const scope = manager ? manager.toUpperCase() : String(username).toUpperCase();

  const clearAll = () => { setProject(""); setManager(""); };

  const handleExport = async () => {
    setExporting(true);
    setNotice("");
    try {
      await exportXlsx({
        filename: `Unbilled ${scope} ${new Date().toISOString().slice(0, 10)}`,
        sheet: "Unbilled",
        title: `Unbilled — ${scope}${project ? ` — ${project}` : ""}`,
        columns: [
          ...(isFd ? [{ key: "username", label: "Manager", type: "text" }] : []),
          { key: "project_name",   label: "Project",        type: "text" },
          { key: "billing_client", label: "Billing client", type: "text" },
          { key: "status",         label: "Status",         type: "text" },
          { key: "revenue",        label: "Revenue",        type: "money" },
          { key: "total_billed",   label: "Billed",         type: "money" },
          { key: "unbilled",       label: "Unbilled",       type: "money" },
          { key: "spent",          label: "Spent",          type: "money" },
          { key: "profit",         label: "Profit",         type: "money" },
          { key: "profit_percent", label: "Margin %",       type: "number" },
          { key: "received",       label: "Received",       type: "money" },
          { key: "a_r",            label: "A/R",            type: "money" },
          { key: "comments",       label: "Comments",       type: "text" },
        ],
        rows: sorted,
        totals: {
          revenue: totals.revenue,
          total_billed: totals.billed,
          unbilled: totals.unbilled,
          profit: totals.profit,
        },
      });
    } catch (e) {
      setNotice(e.message);
    } finally {
      setExporting(false);
    }
  };

  if (loading) return <main className="page"><Loading label="Loading unbilled…" /></main>;
  if (error) return <main className="page"><ErrorState message={error} onRetry={load} /></main>;

  const billedPct = totals.revenue > 0 ? (totals.billed / totals.revenue) * 100 : 0;

  return (
    <main className="page">
      <PageHeader
        title="Unbilled"
        subtitle={`Revenue recognised but not yet invoiced — ${String(username).toUpperCase()}`}
      >
        {isFd && (
          <Field label="Manager">
            <Select
              value={manager}
              onChange={(v) => { setManager(v); setProject(""); }}
              options={[
                { value: "", label: "All managers" },
                ...managers.map((m) => ({ value: m, label: m.toUpperCase() })),
              ]}
              style={{ minWidth: 130 }}
            />
          </Field>
        )}
        <Field label="Project">
          <Select
            value={project}
            onChange={setProject}
            options={[{ value: "", label: "All projects" }, ...projects.map((p) => ({ value: p, label: p }))]}
            style={{ maxWidth: 250 }}
          />
        </Field>
        <button className="btn btn--danger" onClick={clearAll}>Clear all</button>
        <button className="btn" onClick={handleExport} disabled={exporting || !sorted.length}>
          {exporting ? "Preparing…" : "Export to Excel"}
        </button>
      </PageHeader>

      <div className="stack">
        <div className="stat-grid">
          <Stat label="Revenue" accent="var(--violet)" value={money0(totals.revenue)}
                meta={`${filtered.length} project${filtered.length === 1 ? "" : "s"}`} />
          <Stat label="Billed" accent="var(--pos)" value={money0(totals.billed)}
                share={`${billedPct.toFixed(0)}%`} meta="invoiced to date" />
          <Stat label="Unbilled" accent={totals.unbilled > 0 ? "var(--warn)" : "var(--pos)"}
                value={money0(totals.unbilled)}
                meta={totals.revenue > 0 ? `${pct(totals.unbilled, totals.revenue, 0)} of revenue` : "—"} />
        </div>

        {notice && <div className="notice notice--warn">{notice}</div>}

        <Card title="By project" note={`${sorted.length} row${sorted.length === 1 ? "" : "s"}`} flush>
          {!sorted.length ? (
            <EmptyState title="Nothing unbilled" hint="Every project in this selection is fully invoiced." />
          ) : (
            <div className="tbl-wrap tbl-wrap--flush">
              <table className="tbl">
                <thead>
                  <tr>
                    {isFd && (
                      <SortTh id="username" sort={sort} setSort={setSort} style={{ width: 72 }}>Manager</SortTh>
                    )}
                    <SortTh id="project_name" sort={sort} setSort={setSort} style={{ minWidth: 190 }}>Project</SortTh>
                    <SortTh id="billing_client" sort={sort} setSort={setSort} style={{ minWidth: 170 }}>Billing client</SortTh>
                    <SortTh id="status" sort={sort} setSort={setSort} style={{ width: 84 }}>Status</SortTh>
                    <SortTh id="revenue" sort={sort} setSort={setSort} numeric style={{ width: 128 }}>Revenue</SortTh>
                    <SortTh id="total_billed" sort={sort} setSort={setSort} numeric style={{ width: 128 }}>Billed</SortTh>
                    <th style={{ width: 92 }}>Progress</th>
                    <SortTh id="unbilled" sort={sort} setSort={setSort} numeric style={{ width: 128 }}>Unbilled</SortTh>
                    <SortTh id="profit" sort={sort} setSort={setSort} numeric style={{ width: 124 }}>Profit</SortTh>
                    <th className="col-flex" style={{ minWidth: 240 }}>Comments</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((r) => {
                    const revenue = num(r.revenue);
                    const billed = num(r.total_billed);
                    const unbilled = num(r.unbilled);
                    const share = revenue > 0 ? (billed / revenue) * 100 : 0;
                    return (
                      <tr key={r.id}>
                        {isFd && (
                          <td>
                            <ManagerCell
                              row={r}
                              dataset="unbilled"
                              label={r.project_name}
                              managers={allManagers}
                              editable
                              onChange={applyReallocation}
                            />
                          </td>
                        )}
                        <td className="truncate" title={r.project_name}>{r.project_name}</td>
                        <td className="truncate" title={r.billing_client}>
                          {r.billing_client || <span className="dash">—</span>}
                        </td>
                        <td>
                          {r.status
                            ? <span className={`badge ${r.status === "Active" ? "badge--pos" : "badge--warn"}`}>{r.status}</span>
                            : <span className="dash">—</span>}
                        </td>
                        <td className="num">{money(revenue)}</td>
                        <td className="num">{money(billed)}</td>
                        <td>
                          <Meter
                            value={billed}
                            max={revenue}
                            title={`${share.toFixed(0)}% billed`}
                            color={share >= 99 ? "var(--pos)" : share >= 60 ? "var(--accent)" : "var(--warn)"}
                          />
                        </td>
                        <td className={`num${unbilled > 0 ? " warn-text" : ""}`}>{money(unbilled)}</td>
                        <td className={`num ${num(r.profit) < 0 ? "neg" : "pos"}`}
                            title={r.profit_percent != null ? `${num(r.profit_percent).toFixed(1)}% margin · spent ${money(r.spent)}` : undefined}>
                          {r.profit == null ? <span className="dash">—</span> : money(r.profit)}
                        </td>
                        <td>
                          <div className="cell-edit">
                            <input
                              className="input cell-input"
                              value={r.comments ?? ""}
                              placeholder="Add a note…"
                              onChange={(e) => editComment(r.id, e.target.value)}
                              onBlur={() => commitComment(r)}
                              onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                            />
                            {savingId === r.id && <span className="cell-hint">saving…</span>}
                            {savedId === r.id && <span className="cell-hint cell-hint--ok">saved</span>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={isFd ? 4 : 3}>Total</td>
                    <td className="num">{money(totals.revenue)}</td>
                    <td className="num">{money(totals.billed)}</td>
                    <td>
                      <Meter value={totals.billed} max={totals.revenue} color="var(--accent)" />
                    </td>
                    <td className="num">{money(totals.unbilled)}</td>
                    <td className={`num ${totals.profit < 0 ? "neg" : "pos"}`}>{money(totals.profit)}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </Card>
      </div>
    </main>
  );
}
